import {Injectable} from '@angular/core';
import {StorageService} from './storage.service';
import * as fromAuthUserGql from '../store/entities/uaa/auth-user.graphql';
import {BaseResponse} from '../common/interfaces/global-interfaces';
import {AuthUserProfile, InstitutionAuthUser, UserDetails, UserTypeEnum,} from '../store/entities/uaa/auth-user.model';
import {AuthUserActions} from '../store/entities/uaa/auth-user.actions';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {NgxPermissionsService} from 'ngx-permissions';
import {NotificationService} from './notification.service';
import {AuthService} from './auth.service';
import {GraphqlService} from './graphql.service';
import {InactivityTimerService} from "./inactivity-timer.service";
import {GET_USER_DETAILS} from "../store/entities/uaa/auth-user.graphql";

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  constructor(
    private store: Store<AppState>,
    private storageService: StorageService,
    private graphqlService: GraphqlService,
    private permissionsService: NgxPermissionsService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private inactivityTimerService: InactivityTimerService,
  ) {}


  async authRole() {
    if (this.authService.alreadyLoggedIn()) {
      // start session timeout from here
      this.inactivityTimerService.startTimer();
       await this.getUserProfileAndRoles(); //TODO: UNCOMMENT THIS WHEN THE ENDPOINT IS IMPLEMENTED
    }
  }

  async getAdminDetails() {
    try {
      const response: any = await this.graphqlService.query({
        query: fromAuthUserGql.GET_USER_DETAILS,
      });
      const result: BaseResponse<UserDetails[]> = Object.values(
        Object.values(response)[0]
      )[0];
      if (result?.response?.status) {
        const authUser: InstitutionAuthUser = result?.data?.length ? result?.data?.map(d => {
          return {
            ...d,
            userType: d.accountType
          }
        })[0] : {};
        this.store.dispatch(AuthUserActions.loadAuthUsers({ authUsers: [authUser] }));

        const permsObject = authUser?.role?.permissions;

        this.storageService.set('internalUserType', authUser?.userType);

        const permissions: string[] = !!permsObject ? permsObject?.map(
          (res: any) => res?.permissionCode
        ) : [];

        // this permission is added specifically for routes/endpoints that don't need to bound with a permission.
        permissions.push('NO_PERMISSION');
        if (permissions?.length > 0) {
          console.log('permissions', permissions);
          this.permissionsService.loadPermissions(permissions);
        }
        this.authService.monitorRefreshToken();
      } else {
        this.notificationService.errorSnackbar(result?.response?.message);
        this.authService.logout();
      }
    } catch (e) {
      this.authService.logout();
      this.notificationService.errorSnackbar(e.message);
    }
  }


  async getUserProfileAndRoles() {
    try {
      const response: any = await this.graphqlService.query({
        query: fromAuthUserGql.GET_USER_PROFILE_AND_ROLES,
      });
  
      const result: BaseResponse<InstitutionAuthUser> = Object.values(
        Object.values(response)[0]
      )[0];
      // console.log("-----------------------------");
      // console.log(result);
  
      if (result?.response?.status) {
        const authUser: InstitutionAuthUser = result?.data || {};
        
        // Dispatch user to store if valid
        if (authUser) {
          this.store.dispatch(AuthUserActions.loadAuthUsers({ authUsers: [authUser] }));
          this.storageService.set('internalUserType', authUser?.userType);
        }
        
        // Process permissions
        const permissions = authUser?.role?.permissions?.map(
          (perm: any) => perm?.permissionCode
        ) || [];
        
        permissions.push('NO_PERMISSION'); // Default permission for unrestricted routes
        
        if (permissions.length > 0) {
          this.permissionsService.loadPermissions(permissions);
        }
  
        // Start token monitoring
        this.authService.monitorRefreshToken();
      } else {
        // Handle API error response
        const errorMessage = result?.response?.message || 'Unknown error occurred.';
        this.notificationService.errorSnackbar(errorMessage);
        this.authService.logout();
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage = error?.message || 'Failed to process request.';
      this.notificationService.errorSnackbar(errorMessage);
      this.authService.logout();
    }
  }
  


  async getComplainantProfile() {
    try {
      const response: any = await this.graphqlService.query({
        query: fromAuthUserGql.GET_COMPLAINANT_PROFILE,
      });
      const result: BaseResponse<AuthUserProfile[]> = response?.data?.getComplainantProfile;
      if (result?.response?.status) {
        const authUser: AuthUserProfile = result?.data?.[0];
        this.store.dispatch(AuthUserActions.loadAuthUsers({ authUsers: [authUser] }));

        const permissions = [];

        // this permission is added specifically for routes/endpoints that don't need to bound with a permission.
        permissions.push('NO_PERMISSION');
        if (permissions?.length > 0) {
          console.log('permissions', permissions);
          this.permissionsService.loadPermissions(permissions);
        }
        this.authService.monitorRefreshToken();
      } else {
        this.notificationService.errorSnackbar(result?.response?.message);
        this.authService.logout();
      }
    } catch (e) {
      this.authService.logout();
      this.notificationService.errorSnackbar(e.message);
    }
  }

}
